import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrders, updateOrderStatus } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Bike, MapPin, Phone, User, Package } from 'lucide-react';
import { toast } from 'sonner';
import wsService from '@/services/websocket';

const RiderApp = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    setUser(userData);
    loadOrders(userData.store_id, userData.id);

    // Mark rider as online
    markRiderOnline(userData);

    // WebSocket
    wsService.connect(`rider:store:${userData.store_id}`);
    wsService.on('rider.assigned', () => loadOrders(userData.store_id, userData.id));
    wsService.on('order.updated', () => loadOrders(userData.store_id, userData.id));
    wsService.on('order.cancelled', (data) => {
      setOrders(prev => prev.filter(o => o.id !== data.id));
      toast.info(`Order #${data.order_number} was cancelled`);
    });

    const interval = setInterval(() => loadOrders(userData.store_id, userData.id), 10000);
    
    // Send heartbeat every 60 seconds to keep rider online
    const heartbeatInterval = setInterval(async () => {
      try {
        const apiClient = await import('@/services/api').then(m => m.default);
        await apiClient.post('/riders/heartbeat');
        console.log('💓 Heartbeat sent - rider still online');
      } catch (error) {
        console.error('❌ Heartbeat failed:', error);
      }
    }, 60000);
    
    // Cleanup: Mark rider offline on unmount
    return () => {
      markRiderOffline(userData);
      wsService.disconnect();
      clearInterval(interval);
      clearInterval(heartbeatInterval);
    };
  }, []);

  const markRiderOnline = async (userData) => {
    try {
      const apiClient = await import('@/services/api').then(m => m.default);
      const response = await apiClient.post('/riders/online');
      console.log('✅ Rider marked as online:', response.data);
      toast.success(`${userData.name} is now online!`);
    } catch (error) {
      console.error('❌ Error marking rider online:', error);
      toast.error('Failed to mark rider as online. Please refresh.');
    }
  };

  const markRiderOffline = async (userData) => {
    try {
      const apiClient = await import('@/services/api').then(m => m.default);
      await apiClient.post('/riders/offline');
      console.log('✅ Rider marked as offline');
    } catch (error) {
      console.error('❌ Error marking rider offline:', error);
    }
  };

  const loadOrders = async (storeId, riderId) => {
    try {
      // Only show delivery orders that are assigned or out for delivery
      const allOrders = await getOrders(storeId, null, 100);
      const riderOrders = allOrders.data.filter(o => 
        o.order_type === 'DELIVERY' && 
        o.rider_id === riderId &&
        ['RIDER_ASSIGNED', 'OUT_FOR_DELIVERY'].includes(o.status)
      );
      setOrders(riderOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success('Status updated');
      loadOrders(user.store_id, user.id);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const openMaps = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Bike className="w-8 h-8 mr-3 text-blue-600" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-gray-800">Rider Dashboard</h1>
                <span className="text-xs text-red-600 font-medium">(Designed by Prashant B)</span>
              </div>
              <p className="text-sm text-gray-600">{user?.name}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/login')}>Logout</Button>
        </div>

        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.id} data-testid={`order-${order.order_number}`} className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Order #{order.order_number}</span>
                  <span className="text-sm font-normal text-gray-600">{order.status}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="mb-3">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                      {order.order_type}
                    </span>
                  </div>

                  {order.customer?.name && (
                    <div className="flex items-center mb-2">
                      <User className="w-5 h-5 mr-2 text-gray-500" />
                      <p className="text-sm font-semibold">{order.customer.name}</p>
                    </div>
                  )}

                  {order.customer?.phone && (
                    <div className="flex items-center mb-2">
                      <Phone className="w-5 h-5 mr-2 text-gray-500" />
                      <a href={`tel:${order.customer.phone}`} className="text-sm text-blue-600 font-medium">
                        {order.customer.phone}
                      </a>
                    </div>
                  )}

                  {order.customer?.address && (
                    <div className="flex items-start mb-3">
                      <MapPin className="w-5 h-5 mr-2 text-gray-500 mt-0.5" />
                      <p className="text-sm text-gray-600">{order.customer.address}</p>
                    </div>
                  )}

                  <div className="border-t pt-2 mt-2">
                    <p className="text-xs text-gray-500 mb-1">Items:</p>
                    {order.items.map((item, idx) => (
                      <p key={idx} className="text-sm">{item.qty}x {item.menu_item_name}</p>
                    ))}
                  </div>

                  <div className="flex space-x-2 pt-2">
                    {order.customer?.address && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => openMaps(order.customer.address)}
                      >
                        <MapPin className="w-4 h-4 mr-1" />
                        Navigate
                      </Button>
                    )}
                    {order.status === 'RIDER_ASSIGNED' && (
                      <Button
                        data-testid={`pickup-${order.order_number}`}
                        size="sm"
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleStatusUpdate(order.id, 'OUT_FOR_DELIVERY')}
                      >
                        Picked Up
                      </Button>
                    )}
                    {order.status === 'OUT_FOR_DELIVERY' && (
                      <Button
                        data-testid={`delivered-${order.order_number}`}
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleStatusUpdate(order.id, 'DELIVERED')}
                      >
                        Mark Delivered
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {orders.length === 0 && (
          <div className="text-center py-20">
            <Bike className="w-20 h-20 mx-auto mb-4 text-gray-400" />
            <p className="text-2xl text-gray-500">No deliveries assigned</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiderApp;